// Admin — Gelato Print-on-Demand panel. Lets admins browse the Gelato
// catalog (posters/canvases/etc), inspect product variants, upload print-
// ready PDFs per product slug, and view local orders we've created via
// /api/admin/gelato/orders.
import { useEffect, useRef, useState } from "react";
import { Image as ImageIcon, Loader2, ExternalLink, ShoppingBag, RefreshCw, Upload, FileText, Trash2 } from "lucide-react";
import { apiFetch } from "@/lib/api";

const AUTH_KEY = "geovinum.auth.v1";

function authHeaders() {
  try {
    const raw = localStorage.getItem(AUTH_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    const t = parsed?.token;
    return t ? { Authorization: `Bearer ${t}` } : {};
  } catch {
    return {};
  }
}

async function jget(path) {
  const { ok, data, error } = await apiFetch(`/api${path}`, { headers: authHeaders() });
  if (!ok) throw new Error(error || "Request failed");
  return data;
}

async function jpost(path, body) {
  const { ok, data, error } = await apiFetch(`/api${path}`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(body || {}),
  });
  if (!ok) throw new Error(error || "Request failed");
  return data;
}

const FOCUS_CATALOGS = ["posters", "canvas", "framed-posters", "wall-tapestries"];

const AdminGelatoPage = () => {
  const [catalogs, setCatalogs] = useState([]);
  const [selectedCatalog, setSelectedCatalog] = useState("posters");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loadingCats, setLoadingCats] = useState(true);
  const [loadingProds, setLoadingProds] = useState(false);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const data = await jget("/admin/gelato/catalogs");
        setCatalogs(data.data || data.items || []);
      } catch (e) {
        setError(`Catalogues : ${e.message}`);
      } finally {
        setLoadingCats(false);
      }
    })();
    refreshOrders();
  }, []);

  useEffect(() => {
    if (!selectedCatalog) return;
    setLoadingProds(true);
    jpost(`/admin/gelato/catalogs/${selectedCatalog}/products:search`, { limit: 12 })
      .then((d) => setProducts(d.products || []))
      .catch((e) => setError(`Produits : ${e.message}`))
      .finally(() => setLoadingProds(false));
  }, [selectedCatalog]);

  const refreshOrders = async () => {
    setLoadingOrders(true);
    try {
      const d = await jget("/admin/gelato/orders");
      setOrders(d.items || []);
    } catch (e) {
      // Silent — collection might be empty
      setOrders([]);
    } finally {
      setLoadingOrders(false);
    }
  };

  return (
    <div className="space-y-10" data-testid="admin-gelato">
      <header>
        <div className="flex items-center gap-3 mb-2">
          <ImageIcon size={22} className="text-[color:var(--gv-pink)]" />
          <h1 className="font-display text-3xl text-[color:var(--gv-ink)]">Prints / Gelato</h1>
        </div>
        <p className="text-[14px] text-[color:var(--gv-ink)]/65">
          Print-on-Demand pour les cartes geoVINUM (posters, canvas, encadrés). Le catalogue est servi
          par Gelato — la clé API reste côté serveur.
        </p>
      </header>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-[5px] text-[13px]">
          {error}
        </div>
      )}

      {/* Catalog browser */}
      <section className="bg-white border border-[color:var(--gv-line)] rounded-[6px] p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display text-xl text-[color:var(--gv-ink)]">Catalogue Gelato</h2>
          {loadingCats && <Loader2 size={16} className="animate-spin text-[color:var(--gv-pink)]" />}
        </div>

        {/* Catalog pills */}
        <div className="flex flex-wrap gap-2 mb-6">
          {[...FOCUS_CATALOGS, ...catalogs.map((c) => c.catalogUid).filter((c) => !FOCUS_CATALOGS.includes(c))]
            .slice(0, 20)
            .map((uid) => {
              const cat = catalogs.find((c) => c.catalogUid === uid);
              const title = cat?.title || uid;
              const isActive = selectedCatalog === uid;
              return (
                <button
                  key={uid}
                  onClick={() => setSelectedCatalog(uid)}
                  className={`px-3 py-1.5 rounded-[4px] border text-[12.5px] transition-colors ${
                    isActive
                      ? "border-[color:var(--gv-pink)] bg-[color:var(--gv-pink)]/10 text-[color:var(--gv-pink)]"
                      : "border-[color:var(--gv-line)] text-[color:var(--gv-ink)]/70 hover:border-[color:var(--gv-pink)]"
                  }`}
                  data-testid={`gelato-cat-${uid}`}
                >
                  {title}
                </button>
              );
            })}
        </div>

        {/* Products grid */}
        {loadingProds ? (
          <div className="py-8 text-center text-[color:var(--gv-ink)]/50">
            <Loader2 size={18} className="animate-spin mx-auto text-[color:var(--gv-pink)]" />
          </div>
        ) : products.length === 0 ? (
          <div className="py-8 text-center text-[color:var(--gv-ink)]/50 text-[13.5px]">
            Aucun produit dans ce catalogue (essayez un autre catalogue).
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {products.map((p) => (
              <div
                key={p.productUid}
                className="border border-[color:var(--gv-line)] rounded-[5px] p-4 text-[13px]"
                data-testid={`gelato-product-${p.productUid}`}
              >
                <div className="font-medium text-[color:var(--gv-ink)] mb-2 break-all">{p.productUid}</div>
                <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[12px] text-[color:var(--gv-ink)]/70">
                  {p.attributes &&
                    Object.entries(p.attributes)
                      .filter(([, v]) => v && v !== "none" && v !== "no")
                      .slice(0, 6)
                      .map(([k, v]) => (
                        <div key={k}>
                          <span className="text-[color:var(--gv-ink)]/50">{k}:</span> {v}
                        </div>
                      ))}
                </div>
                {p.supportedCountries?.length > 0 && (
                  <div className="text-[11px] text-[color:var(--gv-ink)]/45 mt-2">
                    {p.supportedCountries.slice(0, 6).join(", ")}
                    {p.supportedCountries.length > 6 && "…"}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Print-ready PDF uploader */}
      <PrintFileUploader />

      {/* Orders */}
      <section className="bg-white border border-[color:var(--gv-line)] rounded-[6px] p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display text-xl text-[color:var(--gv-ink)] flex items-center gap-2">
            <ShoppingBag size={18} /> Commandes Gelato
          </h2>
          <button
            onClick={refreshOrders}
            className="inline-flex items-center gap-1.5 text-[12px] text-[color:var(--gv-ink)]/60 hover:text-[color:var(--gv-pink)]"
            data-testid="gelato-refresh-orders"
          >
            <RefreshCw size={13} /> Rafraîchir
          </button>
        </div>
        {loadingOrders ? (
          <div className="py-8 text-center"><Loader2 size={18} className="animate-spin mx-auto text-[color:var(--gv-pink)]" /></div>
        ) : orders.length === 0 ? (
          <div className="py-8 text-center text-[color:var(--gv-ink)]/50 text-[13.5px]">
            Aucune commande Gelato pour le moment. Quand une commande est créée via{" "}
            <code className="text-[12px] bg-[color:var(--gv-mist)] px-1.5 py-0.5 rounded">POST /api/admin/gelato/orders</code>,
            elle apparaîtra ici.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead className="text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/50 border-b border-[color:var(--gv-line)]">
                <tr>
                  <th className="text-left py-2 px-2">Ref</th>
                  <th className="text-left py-2 px-2">Gelato ID</th>
                  <th className="text-left py-2 px-2">Status</th>
                  <th className="text-left py-2 px-2">Pays</th>
                  <th className="text-left py-2 px-2">Articles</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.order_reference_id} className="border-b border-[color:var(--gv-line)]/50">
                    <td className="py-2 px-2 text-[color:var(--gv-ink)]/85">{o.order_reference_id}</td>
                    <td className="py-2 px-2 text-[color:var(--gv-ink)]/55 text-[12px]">{o.order_id || "—"}</td>
                    <td className="py-2 px-2"><span className="px-2 py-0.5 rounded-full bg-[color:var(--gv-mist)] text-[11.5px]">{o.status}</span></td>
                    <td className="py-2 px-2">{o.country}</td>
                    <td className="py-2 px-2">{o.items_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Help */}
      <section className="text-[12.5px] text-[color:var(--gv-ink)]/55 border-t border-[color:var(--gv-line)] pt-5">
        <p>
          API Gelato v4 — Posters, Canvas, Framed Posters. Webhook entrant :{" "}
          <code className="bg-[color:var(--gv-mist)] px-1.5 py-0.5 rounded">POST /api/webhook/gelato</code> (header
          optionnel <code className="bg-[color:var(--gv-mist)] px-1.5 py-0.5 rounded">signature</code>).
        </p>
        <p className="mt-2">
          <a href="https://dashboard.gelato.com/docs/" target="_blank" rel="noreferrer" className="text-[color:var(--gv-pink)] inline-flex items-center gap-1">
            Documentation Gelato <ExternalLink size={11} />
          </a>
        </p>
      </section>
    </div>
  );
};

export default AdminGelatoPage;

// ---- Print file uploader subcomponent ----
// Pulled from /api/prints/catalog at runtime so we always reflect the
// authoritative server-side catalog.
const PRINTABLE_SLUGS_FALLBACK = [];

const PrintFileUploader = () => {
  const [files, setFiles] = useState({});
  const [counts, setCounts] = useState({});
  const [slugs, setSlugs] = useState(PRINTABLE_SLUGS_FALLBACK);
  const [loading, setLoading] = useState(true);
  const [uploadingSlug, setUploadingSlug] = useState(null);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("");
  const inputRefs = useRef({});

  const refresh = async () => {
    setLoading(true);
    try {
      const [filesRes, catalogRes] = await Promise.all([
        apiFetch(`/api/admin/prints/files`, { headers: authHeaders() }),
        apiFetch(`/api/prints/catalog`),
      ]);
      if (!filesRes.ok) throw new Error(filesRes.error || "Files request failed");
      if (!catalogRes.ok) throw new Error(catalogRes.error || "Catalog request failed");
      setFiles(filesRes.data.latest_per_slug || {});
      setCounts(filesRes.data.counts || {});
      setSlugs((catalogRes.data.items || []).map((i) => i.slug));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); }, []);

  const upload = async (slug, fileObj) => {
    if (!fileObj) return;
    setUploadingSlug(slug);
    setError(null);
    const fd = new FormData();
    fd.append("slug", slug);
    fd.append("file", fileObj);
    const { ok, error: err } = await apiFetch(`/api/admin/prints/files`, {
      method: "POST",
      headers: { ...authHeaders(), origin: window.location.origin },
      body: fd,
    });
    if (!ok) {
      setError(err || "Upload failed");
    } else {
      await refresh();
    }
    setUploadingSlug(null);
  };

  const remove = async (filename) => {
    if (!window.confirm(`Supprimer ${filename} ?`)) return;
    const { ok, error: err } = await apiFetch(`/api/admin/prints/files/${encodeURIComponent(filename)}`, {
      method: "DELETE",
      headers: authHeaders(),
    });
    if (!ok) setError(err || "Delete failed");
    else refresh();
  };

  return (
    <section className="bg-white border border-[color:var(--gv-line)] rounded-[6px] p-6">
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-display text-xl text-[color:var(--gv-ink)] flex items-center gap-2">
          <FileText size={18} /> Fichiers print-ready
        </h2>
        <button onClick={refresh} className="inline-flex items-center gap-1.5 text-[12px] text-[color:var(--gv-ink)]/60 hover:text-[color:var(--gv-pink)]" data-testid="prints-files-refresh">
          <RefreshCw size={13} /> Rafraîchir
        </button>
      </div>
      <p className="text-[13px] text-[color:var(--gv-ink)]/65 mb-4">
        Téléversez le PDF haute résolution pour chaque carte. Le dernier fichier uploadé est utilisé lors de la création des commandes Gelato.
      </p>
      <input
        type="search"
        placeholder={`Filtrer parmi ${slugs.length} cartes…`}
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        className="w-full md:max-w-xs mb-4 px-3 py-2 text-[13px] rounded-[5px] border border-[color:var(--gv-line)] focus:border-[color:var(--gv-pink)] focus:outline-none"
        data-testid="print-files-filter"
      />
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-[13px] px-3 py-2 rounded-[5px] mb-3">
          {error}
        </div>
      )}
      {loading ? (
        <div className="py-6 text-center"><Loader2 size={18} className="animate-spin mx-auto text-[color:var(--gv-pink)]" /></div>
      ) : (
        <div className="space-y-2 max-h-[600px] overflow-y-auto pr-2">
          {slugs
            .filter((s) => !filter || s.toLowerCase().includes(filter.toLowerCase()))
            .map((slug) => {
            const current = files[slug];
            const isUploading = uploadingSlug === slug;
            return (
              <div key={slug} className="flex items-center justify-between gap-4 border border-[color:var(--gv-line)] rounded-[5px] p-4" data-testid={`print-file-row-${slug}`}>
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-[color:var(--gv-ink)] text-[14px] mb-1">{slug}</div>
                  {current ? (
                    <div className="text-[12px] text-[color:var(--gv-ink)]/60 truncate">
                      <a href={current.url} target="_blank" rel="noreferrer" className="text-[color:var(--gv-pink)] hover:underline" data-testid={`print-file-link-${slug}`}>
                        {current.original_name || current.filename}
                      </a>
                      {" · "}{Math.round((current.size_bytes || 0) / 1024)} KB
                      {counts[slug] > 1 && <span className="text-[color:var(--gv-ink)]/40"> · {counts[slug]} versions</span>}
                    </div>
                  ) : (
                    <div className="text-[12px] text-[color:var(--gv-ink)]/50">Aucun fichier — fallback PDF par défaut utilisé.</div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg,.tif,.tiff"
                    ref={(el) => (inputRefs.current[slug] = el)}
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) upload(slug, f);
                      e.target.value = "";
                    }}
                    data-testid={`print-file-input-${slug}`}
                  />
                  <button
                    onClick={() => inputRefs.current[slug]?.click()}
                    disabled={isUploading}
                    className="btn-ghost text-[12px]"
                    data-testid={`print-file-upload-${slug}`}
                  >
                    {isUploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
                    {current ? "Remplacer" : "Téléverser"}
                  </button>
                  {current && (
                    <button onClick={() => remove(current.filename)} className="text-[12px] text-[color:var(--gv-ink)]/40 hover:text-red-500 p-1.5" aria-label="Supprimer" data-testid={`print-file-delete-${slug}`}>
                      <Trash2 size={13} />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};
